import axios, { AxiosError } from 'axios';

import { isHeadlessPrintRoute } from '../../utils/printRoute';
import { markApiFailure, markApiSuccess } from '../../utils/connectivityStore';

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  // Fail fast when the server is unreachable instead of hanging indefinitely.
  // 30s covers slow file uploads and heavy reports while still bounding waits.
  timeout: 30000,
});

api.interceptors.response.use(
  (response) => {
    // A successful response means the server is reachable — clear any
    // "server unavailable" state even if a prior call had tripped it.
    markApiSuccess();
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // An expired/invalid impersonation token must not destroy the admin's real
      // session: drop only the impersonation keys, reinstall the admin token, and
      // reload so the app rehydrates as the admin instead of bouncing to /login.
      if (sessionStorage.getItem('impersonation_token')) {
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonation_user');
        const adminToken = localStorage.getItem('token');
        if (adminToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
          window.location.reload();
          return Promise.reject(error);
        }
      }

      // Clear token and storage on auth error
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];

      // Redirect to login — but NOT on the headless PDF print route. There, a hard
      // navigation to /login means nothing ever flips window.__PRINT_READY, so the
      // backend's readiness poll times out and the report 5xx's (no Go fallback).
      // Skipping the redirect lets PrintMoldOrderReport's own catch render a visible
      // error that the headless worker captures as a (valid) error PDF instead.
      const path = window.location.pathname;
      if (path !== '/login' && !isHeadlessPrintRoute(path)) {
        window.location.href = '/login';
      }
    }

    // Report connectivity-affecting failures so the UI can show a persistent,
    // truthful state instead of letting every list render "empty".
    if (isConnectivityFailure(error)) {
      markApiFailure();
    }

    return Promise.reject(error);
  },
);

/**
 * True for failures that indicate the server could not be reached or could not
 * serve any response: no response at all (ERR_NETWORK), a timeout, or a 5xx.
 * 4xx responses mean the server IS reachable — the request was just rejected —
 * so they don't trip the connectivity banner.
 */
function isConnectivityFailure(error: AxiosError): boolean {
  const status = error.response?.status;
  if (status === undefined) {
    // No response received: network error, timeout (ECONNABORTED), or DNS.
    return true;
  }
  return status >= 500;
}

export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export default api;
